import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { CroppedImageView } from '@bingads-webui-campaign-react/cropped-image-view';

import {
  getBingSearchTextPlacementStyles,
  getBingSearchTextDesktopPlacementStyles,
  getBingSearchTextMobilePlacementStyles,
} from '../styles';
import { withUAPLThemedStyles } from '../with-themed-styles';
import {
  AdLabel,
  PlacementHeadline,
  PlacementFinalUrl,
  PlacementDescription,
  PlacementContentContainer,
} from '../shared-components';
import { bingDesktopHeaderImage } from './media/bing-desktop-header';

const BingSearchPlacementBase = ({
  placementData, // UAPVMSingleImagePlacement
  longHeadline,
  finalUrl,
  displayUrl,
  description,
  businessName,
  isClickDisabled,
  showLogo,
  logo,
  i18n, // may need to set window._TL_ in other platforms
  classes,
  children,
  adExtensionContainerClassName,
}) => (
  <div className={classnames(classes.placementCard, 'ad-placement-card-preview')}>
    <PlacementContentContainer isClickDisabled={isClickDisabled} finalUrl={finalUrl} displayUrl={displayUrl}>
      <div className={classnames(classes.placementTextContainer)}>
        {showLogo && logo &&
          <div className="logo-and-text-container">
            <div className={classnames(classes.placementLogoContainer, 'logo-container')}>
              <CroppedImageView
                resizeImageHeight={(placementData && placementData.placementMeta && placementData.placementMeta.logoHeight) || 24}
                src={logo.Src}
                originalWidth={logo.Width}
                originalHeight={logo.Height}
                SourceX={logo.CropSettings.SourceX}
                SourceY={logo.CropSettings.SourceY}
                SourceWidth={logo.CropSettings.SourceWidth}
                SourceHeight={logo.CropSettings.SourceHeight}
                aspectRatio={1}
              />
            </div>
            <div className="logo-text">
              <div className="logo-business-name">{businessName}</div>
              <PlacementFinalUrl
                finalUrl={finalUrl}
                displayUrl={displayUrl}
                isMultiLine
                placementFinalUrlClassName={classnames(classes.placementFinalUrl, 'final-url')}
              />
            </div>
          </div>
        }
        <PlacementHeadline
          headlineText={longHeadline}
          isMultiLine
          placementHeadlineClassName={classnames(classes.placementLongHeadline, 'long-headline')}
        />
        {(!showLogo || !logo) &&
          <PlacementFinalUrl
            finalUrl={finalUrl}
            displayUrl={displayUrl}
            isMultiLine
            placementFinalUrlClassName={classnames(classes.placementFinalUrl, 'final-url')}
          />
        }
        <div className={classnames(classes.placementDescriptionContainer, 'business-name-with-ads')}>
          <AdLabel i18n={i18n} adsLabelClassName={classes.placementAdsLabel} />
          <PlacementDescription
            description={description}
            isMultiLine
            placementDescriptionClassName={classes.placementDescription}
          />
        </div>
      </div>
      {children && (
        <div className={classnames(adExtensionContainerClassName, classes.adExtensionContainer)}>
          {children}
        </div>
      )}
    </PlacementContentContainer>
  </div>
);

BingSearchPlacementBase.propTypes = {
  placementData: PropTypes.shape({}).isRequired, // UAPVMSingleImagePlacement
  longHeadline: PropTypes.string,
  finalUrl: PropTypes.string,
  displayUrl: PropTypes.string,
  description: PropTypes.string,
  businessName: PropTypes.string,
  isClickDisabled: PropTypes.bool,
  showLogo: PropTypes.bool,
  logo: PropTypes.shape({
    Src: PropTypes.string,
    Width: PropTypes.number,
    Height: PropTypes.number,
    CropSettings: PropTypes.shape({
      SourceX: PropTypes.number,
      SourceY: PropTypes.number,
      SourceWidth: PropTypes.number,
      SourceHeight: PropTypes.number,
    }),
  }),
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  classes: PropTypes.shape({
    placementCard: PropTypes.string,
    placementTextContainer: PropTypes.string,
    placementAdsLabel: PropTypes.string,
    placementShortHeadline: PropTypes.string,
    placementLongHeadline: PropTypes.string,
    placementFinalUrl: PropTypes.string,
    placementDescriptionContainer: PropTypes.string,
    placementDescription: PropTypes.string,
  }).isRequired,
  adExtensionContainerClassName: PropTypes.string,
};

BingSearchPlacementBase.defaultProps = {
  longHeadline: undefined,
  finalUrl: undefined,
  displayUrl: undefined,
  description: undefined,
  businessName: undefined,
  isClickDisabled: false,
  showLogo: false,
  logo: undefined,
  adExtensionContainerClassName: undefined,
};

export const AdPlacementBingSearchText = withUAPLThemedStyles(
  props => (
    <BingSearchPlacementBase {...props} showLogo />
  ),
  getBingSearchTextPlacementStyles,
  { componentName: 'AdPlacementBingSearchText' }
);

export const AdPlacementBingSearchTextDesktop = withUAPLThemedStyles(
  ({ classes, i18n, ...props }) => (
    <div className={classes.bingSearchDesktopPlacementRoot}>
      <div className="header-image">
        <img src={bingDesktopHeaderImage} alt={i18n.getString(_TL_('Header Image'))} />
      </div>
      <BingSearchPlacementBase {...props} classes={classes} i18n={i18n} />
    </div>
  ),
  getBingSearchTextDesktopPlacementStyles,
  { componentName: 'AdPlacementBingSearchTextDesktop' }
);

export const AdPlacementBingSearchTextMobile = withUAPLThemedStyles(
  props => (
    <div className={props.classes.bingSearchMobilePlacementRoot}>
      <div className="bing-search-mobile-placement-parent">
        <BingSearchPlacementBase {...props} />
      </div>
    </div>
  ),
  getBingSearchTextMobilePlacementStyles,
  { componentName: 'AdPlacementBingSearchTextMobile' }
);
